import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AccountSwitchHeader from '@/components/feature/AccountSwitchHeader';
import PeriodPicker, { monthRange } from '@/components/feature/PeriodPicker';
import type { Period } from '@/components/feature/PeriodPicker';
import { getVoucherDetails } from '@/api';
import type { AccountDetails } from '@/api';

export default function VoucherDetailsPage() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>(() => monthRange(0));
  const [voucherDetails, setVoucherDetails] = useState<AccountDetails | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    getVoucherDetails(1, 50)
      .then((res) => {
        if (mounted) setVoucherDetails(res);
      })
      .catch((err) => {
        if (mounted) setError(err instanceof Error ? err.message : '加载失败');
      });
    return () => {
      mounted = false;
    };
  }, []);

  //按账期过滤记录并统计（流水：发放=收入，使用=支出）
  const periodRecords = voucherDetails
    ? voucherDetails.records.filter((r) => {
        const day = r.date.replace(/\//g, '-').slice(0, 10);
        return day >= period.start && day <= period.end;
      })
    : [];
  const periodIncome = periodRecords.reduce((s, r) => (r.type === '使用' ? s : s + r.amount), 0);
  const periodExpense = periodRecords.reduce((s, r) => (r.type === '使用' ? s + r.amount : s), 0);

  //加载中/失败
  if (!voucherDetails) {
    return (
      <div className="mobile-container pb-8">
        <AccountSwitchHeader current="voucher" />
        <div className="flex flex-col items-center justify-center py-24">
          {error ? (
            <>
              <i className="ri-wifi-off-line text-foreground-300 text-4xl mb-3" />
              <p className="text-foreground-400 text-sm">{error}</p>
            </>
          ) : (
            <>
              <i className="ri-loader-4-line text-primary-400 text-3xl animate-spin mb-3" />
              <p className="text-foreground-400 text-sm">加载中...</p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-container pb-8">
      {/* Header */}
      <AccountSwitchHeader current="voucher" />

      <div className="px-4 space-y-4 pt-4">
        {/* Balance Card */}
        <div className="relative rounded-2xl overflow-hidden">
          {/* Gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-orange-400 via-red-400 to-rose-500"></div>
          {/* Decorative circles */}
          <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full bg-white/10"></div>
          <div className="absolute -bottom-8 -right-2 w-36 h-36 rounded-full bg-white/8"></div>
          <div className="absolute top-4 right-14 w-14 h-14 rounded-full bg-white/12"></div>

          <div className="relative px-5 py-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-white/80 text-xs font-medium tracking-wide mb-1">代金券余额</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-white text-xl font-medium">¥</span>
                  <span className="text-white text-4xl font-bold tracking-tight">
                    {Number(voucherDetails.balance).toFixed(2)}
                  </span>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1.5 mt-1">
                <div className="flex items-center gap-1.5 bg-white/20 rounded-full px-3 py-1">
                  <i className="ri-arrow-down-line text-white text-xs"></i>
                  <span className="text-white text-xs font-medium">累计收入</span>
                </div>
                <span className="text-white font-semibold text-sm pr-1">
                  +¥{Number(voucherDetails.income).toFixed(2)}
                </span>
              </div>
            </div>

            {/* Stats row */}
            <div className="mt-4 flex items-center gap-3">
              <div className="flex-1 bg-white/15 rounded-xl px-3 py-2">
                <p className="text-white/70 text-xs mb-0.5">本期收入</p>
                <p className="text-white font-semibold text-sm">+¥{periodIncome.toFixed(2)}</p>
              </div>
              <div className="flex-1 bg-white/15 rounded-xl px-3 py-2">
                <p className="text-white/70 text-xs mb-0.5">本期支出</p>
                <p className="text-white font-semibold text-sm">¥{periodExpense.toFixed(2)}</p>
              </div>
              <div className="flex-1 bg-white/15 rounded-xl px-3 py-2">
                <p className="text-white/70 text-xs mb-0.5">笔数</p>
                <p className="text-white font-semibold text-sm">{periodRecords.length} 笔</p>
              </div>
            </div>
          </div>
        </div>

        {/* Rules Card */}
        <div className="bg-amber-50 border border-amber-200/70 rounded-2xl px-4 py-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-information-line text-amber-500 text-base"></i>
            </div>
            <span className="text-amber-700 text-xs font-semibold">代金券储备说明</span>
          </div>
          <ul className="space-y-2">
            {[
              '每笔完成交易单按交易额 0.3% 自动存入，无需手动操作',
              '累积至 ¥3,980 且直推满 3 人，可申请兑换实物代金券',
              '兑换后代金券存入「优惠券账户」，可在购物时抵扣使用',
              '储备积分长期有效，无过期时间',
            ].map((rule, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="mt-0.5 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5"></span>
                <span className="text-amber-700/90 text-xs leading-relaxed">{rule}</span>
              </li>
            ))}
          </ul>
          <button
            className="mt-3 flex items-center gap-1.5 text-primary-500 text-xs font-medium cursor-pointer hover:text-primary-600 transition-colors"
            onClick={() => navigate('/voucher-mall')}
          >
            <i className="ri-store-2-line text-sm"></i>
            <span>前往兑换商城</span>
            <i className="ri-arrow-right-s-line text-sm"></i>
          </button>
        </div>

        {/* Period Selector */}
        <PeriodPicker period={period} onChange={setPeriod} />

        {/* Section Title */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-1 h-4 rounded-full bg-primary-500"></div>
            <span className="text-sm font-semibold text-foreground-800">代金券明细</span>
          </div>
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span>
              收入：
              <span className="text-primary-500 font-semibold">+¥{periodIncome.toFixed(2)}</span>
            </span>
            <span>
              支出：
              <span className="text-foreground-600 font-medium">¥{periodExpense.toFixed(2)}</span>
            </span>
          </div>
        </div>

        {/* Records List */}
        <div className="space-y-3">
          {periodRecords.map((record) => (
            <div
              key={record.id}
              className="bg-white rounded-2xl px-4 py-4 border border-background-100 hover:border-background-200 transition-colors"
            >
              {/* Top row */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-50">
                    <i className="ri-coupon-line text-primary-500 text-sm"></i>
                  </div>
                  <div>
                    <span className="text-foreground-800 text-sm font-semibold">{record.type}</span>
                    <div className="flex items-center gap-1 mt-0.5">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-green-50 text-green-600 text-xs font-medium">收入</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-primary-500 text-lg font-bold">+¥{Number(record.amount).toFixed(2)}</p>
                </div>
              </div>

              {/* Divider */}
              <div className="h-px bg-background-100 mb-3"></div>

              {/* Detail rows */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-foreground-400">买家姓名</span>
                  <span className="text-foreground-700 font-medium">{record.buyerName}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-foreground-400">订单号</span>
                  <span className="text-primary-500 font-medium tracking-tight">{record.orderNumber}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-foreground-400">订单成交额</span>
                  <span className="text-foreground-700 font-semibold">¥{Number(record.tradeAmount).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-foreground-400">时间</span>
                  <span className="text-foreground-500">{record.date}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom tip */}
        <div className="flex items-center justify-center gap-2 py-3">
          <div className="h-px w-12 bg-background-200"></div>
          <span className="text-foreground-400 text-xs">已显示全部记录</span>
          <div className="h-px w-12 bg-background-200"></div>
        </div>
      </div>
    </div>
  );
}